import ReceiptHiddenSection from './ReceiptHiddenSection';
import {
  KoobButton,
  KoobBadge,
  KoobInputDateRange,
  KoobInputLabel,
} from '@koob/ui';
import { Form, Formik } from 'formik';
import { TextField, SelectField } from 'components/Fields';
import { KoobParagraph, KoobTitle } from '@koob/ui';
import { useTranslation } from 'react-i18next';
import { toDateString } from '@koob/dates';
import { useState } from 'react';
import { PRICING_CATEGORIES } from '@koob/utils';

const toDate = (v) => (v ? (v?.toDate?.() ?? new Date(v)) : null);

const CATEGORY_OPTIONS = PRICING_CATEGORIES.map(({ id, label }) => ({
  value: id,
  label,
}));

export default function ReceiptContentManualItem({ setManualItems, currency }) {
  const { t } = useTranslation('trips');

  const [startAt, setStartAt] = useState(new Date());
  const [endAt, setEndAt] = useState(new Date());

  const addManualItem = (values, { resetForm }) => {
    const price = parseFloat(values.price);
    setManualItems((prev) => [
      ...prev,
      {
        ...values,
        category: values.category?.value ?? values.category,
        id: crypto.randomUUID(),
        type: 'manual',
        isGeneric: true,
        startAt,
        endAt,
        priceWithoutMarkup: price,
        price,
        currency,
      },
    ]);
    resetForm();
    setStartAt(null);
    setEndAt(null);
  };

  return (
    <ReceiptHiddenSection className="mt-3">
      <KoobTitle size="md">
        <div className="flex space-x-3 items-center">
          <div>{t('receipt.manualItems.title')}</div>
          <KoobBadge>{t('receipt.optional')}</KoobBadge>
        </div>
      </KoobTitle>

      <KoobParagraph size="sm">
        {t('receipt.manualItems.description')}
      </KoobParagraph>

      <div className="my-3">
        <Formik
          initialValues={{
            name: '',
            category: '',
            pax: '',
            price: '',
          }}
          validate={(values) => {
            const errors = {};

            if (!values.name) {
              errors.name = t('misc:required');
            }

            if (!values.category) {
              errors.category = t('misc:required');
            }

            if (!values.price) {
              errors.price = t('misc:required');
            }

            if (!values.pax || Number(values.pax) < 1) {
              errors.pax = t('misc:required');
            }

            return errors;
          }}
          validateOnChange={true}
          onSubmit={addManualItem}
        >
          {({ isValid }) => (
            <Form>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <TextField
                    label={t('receipt.manualItems.itemName')}
                    name="name"
                    placeholder="e.g. Airport transfer"
                    required
                  />
                </div>

                <div className="w-48">
                  <SelectField
                    label={t('receipt.manualItems.category')}
                    name="category"
                    options={CATEGORY_OPTIONS}
                    required
                  />
                </div>

                <div>
                  <KoobInputLabel
                    label={t('receipt.bookedItems.columnDates')}
                    required
                    className="w-auto"
                  />
                  <KoobInputDateRange
                    presets={[]}
                    value={{
                      from: toDate(startAt) ?? undefined,
                      to: toDate(endAt) ?? undefined,
                    }}
                    onChange={(range) => {
                      setStartAt(range ? (toDateString(range.from) ?? '') : '');
                      setEndAt(range ? (toDateString(range.to) ?? '') : '');
                    }}
                  />
                </div>

                <div className="w-20">
                  <TextField
                    label={t('receipt.bookedItems.columnPax')}
                    name="pax"
                    type="number"
                    min="1"
                    required
                  />
                </div>

                <div className="w-36">
                  <TextField
                    label={`${t('receipt.bookedItems.columnTotal')} (${currency?.toUpperCase()})`}
                    name="price"
                    type="number"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="basis-full flex justify-end">
                  <KoobButton
                    variant="primary"
                    type="submit"
                    disabled={!isValid || !startAt || !endAt}
                  >
                    <i className="fa fa-plus mr-1" />
                    {t('receipt.manualItems.addItem')}
                  </KoobButton>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </ReceiptHiddenSection>
  );
}
